import { useState, useRef, useCallback, useEffect } from 'react'
import { useFileStore } from '../stores/fileStore'
import { useToastStore } from '../stores/toastStore'
import { X, Upload, File, Image, Video, Music, FileText, CheckCircle, AlertCircle } from 'lucide-react'
import { formatFileSize, getFileType } from '../utils/api'

interface UploadModalProps {
  isOpen: boolean
  onClose: () => void
  currentPath: string
}

interface UploadFile {
  file: File
  id: string
  progress: number
  status: 'pending' | 'uploading' | 'completed' | 'error'
  error?: string
  uploadedBytes?: number
  totalBytes?: number
  speed?: number
  eta?: number
}

export function UploadModal({ isOpen, onClose, currentPath }: UploadModalProps) {
  const [files, setFiles] = useState<UploadFile[]>([])
  const [isPublic, setIsPublic] = useState(true)
  const [isDragging, setIsDragging] = useState(false)
  const [globalProgress, setGlobalProgress] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { uploadFileWithProgress } = useFileStore()
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map())

  // Clear any lingering toasts when modal opens
  useEffect(() => {
    if (isOpen) {
      useToastStore.getState().clearToasts()
    }
  }, [isOpen])

  // Calculate global progress
  useEffect(() => {
    if (files.length === 0) {
      setGlobalProgress(0)
      return
    }

    const totalProgress = files.reduce((sum, file) => sum + file.progress, 0)
    setGlobalProgress(totalProgress / files.length)
  }, [files])

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles) return

    const newFiles: UploadFile[] = Array.from(selectedFiles).map(file => ({
      file,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      progress: 0,
      status: 'pending',
      totalBytes: file.size,
      uploadedBytes: 0
    }))

    setFiles(prev => [...prev, ...newFiles])
    
    // Auto start upload for new files
    setTimeout(() => {
      newFiles.forEach(fileItem => {
        uploadSingleFile(fileItem)
      })
    }, 100)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    handleFileSelect(e.dataTransfer.files)
  }, [handleFileSelect])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false)
    }
  }, [])

  const removeFile = (id: string) => {
    // Cancel upload if in progress
    const controller = abortControllersRef.current.get(id)
    if (controller) {
      controller.abort()
      abortControllersRef.current.delete(id)
    }
    
    setFiles(prev => prev.filter(f => f.id !== id))
  }

  const formatSpeed = (bytesPerSecond: number): string => {
    if (bytesPerSecond < 1024) return `${bytesPerSecond.toFixed(0)} B/s`
    if (bytesPerSecond < 1024 * 1024) return `${(bytesPerSecond / 1024).toFixed(1)} KB/s`
    return `${(bytesPerSecond / (1024 * 1024)).toFixed(1)} MB/s`
  }

  const formatETA = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`
    return `${Math.round(seconds / 3600)}h`
  }

  const uploadSingleFile = async (fileItem: UploadFile) => {
    const controller = new AbortController()
    abortControllersRef.current.set(fileItem.id, controller)

    setFiles(prev => prev.map(f => 
      f.id === fileItem.id 
        ? { ...f, status: 'uploading', progress: 0 }
        : f
    ))

    const startTime = Date.now()

    try {
      // Create progress callback
      const onProgress = (uploadedBytes: number, totalBytes: number) => {
        const progress = Math.round((uploadedBytes / totalBytes) * 100)
        const elapsed = (Date.now() - startTime) / 1000
        const speed = uploadedBytes / elapsed
        const eta = (totalBytes - uploadedBytes) / speed

        setFiles(prev => prev.map(f => 
          f.id === fileItem.id 
            ? { 
                ...f, 
                progress,
                uploadedBytes,
                totalBytes,
                speed,
                eta: eta > 0 ? eta : 0
              }
            : f
        ))
      }

      // Use enhanced upload function with progress
      await uploadFileWithProgress(fileItem.file, isPublic, onProgress, controller.signal, currentPath)

      setFiles(prev => prev.map(f => 
        f.id === fileItem.id 
          ? { ...f, status: 'completed', progress: 100 }
          : f
      ))

    } catch (error) {
      if (typeof error === 'object' && error !== null && 'name' in error && (error as any).name === 'AbortError') {
        // Upload was cancelled
        return
      }

      setFiles(prev => prev.map(f => 
        f.id === fileItem.id 
          ? { 
              ...f, 
              status: 'error', 
              error: error instanceof Error ? error.message : 'Upload failed' 
            }
          : f
      ))
    } finally {
      abortControllersRef.current.delete(fileItem.id)
    }
  }

  const retryFile = (fileItem: UploadFile) => {
    uploadSingleFile(fileItem)
  }

  const handleClose = () => {
    const hasUploading = files.some(f => f.status === 'uploading')
    
    if (hasUploading) {
      if (confirm('There are files still uploading. Are you sure you want to close?')) {
        // Cancel all uploads
        abortControllersRef.current.forEach(controller => controller.abort())
        abortControllersRef.current.clear()
        onClose()
        setFiles([])
        setIsPublic(true)
      }
    } else {
      onClose()
      setFiles([])
      setIsPublic(true)
    }
  }

  // Auto-close when all uploads complete
  useEffect(() => {
    const completedFiles = files.filter(f => f.status === 'completed')
    const errorFiles = files.filter(f => f.status === 'error')
    const totalFiles = files.length

    if (totalFiles > 0 && (completedFiles.length + errorFiles.length) === totalFiles) {
      // All uploads finished
      setTimeout(() => {
        useToastStore.getState().clearToasts()
        
        if (completedFiles.length > 0) {
          useToastStore.getState().addToast({
            type: 'success',
            message: `Successfully uploaded ${completedFiles.length} file${completedFiles.length !== 1 ? 's' : ''}`,
          })
          
          // Refresh storage counter
          if ((window as any).refreshStorageUsage) {
            (window as any).refreshStorageUsage()
          }
        }
        
        if (errorFiles.length > 0) {
          useToastStore.getState().addToast({
            type: 'error',
            message: `Failed to upload ${errorFiles.length} file${errorFiles.length !== 1 ? 's' : ''}`,
          })
        }
      }, 1000)
    }
  }, [files])

  const getFileIcon = (file: File) => {
    const type = getFileType(file.type)
    switch (type) {
      case 'image':
        return <Image className="h-6 w-6 text-green-400" />
      case 'video':
        return <Video className="h-6 w-6 text-purple-400" />
      case 'audio':
        return <Music className="h-6 w-6 text-pink-400" />
      case 'text':
        return <FileText className="h-6 w-6 text-gray-400" />
      default:
        return <File className="h-6 w-6 text-gray-400" />
    }
  }

  if (!isOpen) return null

  const hasActiveUploads = files.some(f => f.status === 'uploading')
  const completedCount = files.filter(f => f.status === 'completed').length
  const errorCount = files.filter(f => f.status === 'error').length

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-3xl rounded-xl bg-dark-800 p-6 shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex-1">
            <h2 className="text-xl font-bold text-white">Upload Files</h2>
            <p className="text-dark-400">
              Upload to: {currentPath || 'Root folder'}
            </p>
            {files.length > 0 && (
              <div className="mt-2 flex items-center space-x-4 text-sm">
                <span className="text-green-400">{completedCount} completed</span>
                {errorCount > 0 && <span className="text-red-400">{errorCount} failed</span>}
                {hasActiveUploads && (
                  <span className="text-blue-400">
                    {files.filter(f => f.status === 'uploading').length} uploading
                  </span>
                )}
              </div>
            )}
          </div>
          
          {/* Global Progress */}
          {files.length > 0 && hasActiveUploads && (
            <div className="mx-6 w-32">
              <div className="mb-1 h-2 rounded-full bg-dark-600">
                <div
                  className="h-2 rounded-full bg-primary-500 transition-all duration-300"
                  style={{ width: `${globalProgress}%` }}
                />
              </div>
              <p className="text-xs text-center text-dark-400">
                {Math.round(globalProgress)}% overall
              </p>
            </div>
          )}
          
          <button
            onClick={handleClose}
            className="rounded-lg p-2 text-dark-400 hover:bg-dark-700 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Drop Zone */}
        <div
          className={`mb-6 rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
            isDragging
              ? 'border-primary-500 bg-primary-500/10'
              : 'border-dark-600 hover:border-dark-500'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <Upload className="mx-auto mb-4 h-12 w-12 text-dark-400" />
          <h3 className="mb-2 text-lg font-semibold text-white">
            {isDragging ? 'Drop files here' : 'Drag & drop files'}
          </h3>
          <p className="mb-4 text-dark-400">
            or{' '}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="text-primary-400 hover:text-primary-300"
            >
              browse files
            </button>
          </p>
          <p className="text-sm text-dark-500">
            No file size limit • All file types supported • Auto-upload enabled
          </p>
        </div>

        {/* Privacy Setting */}
        <div className="mb-6">
          <label className="text-sm font-medium text-dark-200">Visibility</label>
          <div className="mt-2 flex space-x-4">
            <button
              onClick={() => setIsPublic(true)}
              className={`flex items-center space-x-2 rounded-lg px-4 py-2 ${
                isPublic
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
              }`}
            >
              <div className={`h-2 w-2 rounded-full ${isPublic ? 'bg-green-400' : 'bg-dark-500'}`} />
              <span>Public</span>
            </button>
            <button
              onClick={() => setIsPublic(false)}
              className={`flex items-center space-x-2 rounded-lg px-4 py-2 ${
                !isPublic
                  ? 'bg-red-500/20 text-red-400'
                  : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
              }`}
            >
              <div className={`h-2 w-2 rounded-full ${!isPublic ? 'bg-red-400' : 'bg-dark-500'}`} />
              <span>Private</span>
            </button>
          </div>
        </div>

        {/* File List */}
        {files.length > 0 && (
          <div className="flex-1 min-h-0">
            <div className="max-h-96 space-y-3 overflow-y-auto pr-2">
              {files.map((fileItem) => (
                <div
                  key={fileItem.id}
                  className="rounded-lg bg-dark-700/50 p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      {getFileIcon(fileItem.file)}
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-white truncate">{fileItem.file.name}</p>
                        <p className="text-sm text-dark-400">
                          {formatFileSize(fileItem.file.size)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      {fileItem.status === 'completed' && (
                        <CheckCircle className="h-5 w-5 text-green-400" />
                      )}

                      {fileItem.status === 'error' && (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => retryFile(fileItem)}
                            className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded hover:bg-red-500/30"
                          >
                            Retry
                          </button>
                          <AlertCircle className="h-5 w-5 text-red-400" />
                        </div>
                      )}

                      <button
                        onClick={() => removeFile(fileItem.id)}
                        className="rounded-md p-1 text-dark-400 hover:bg-dark-600 hover:text-white"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Progress Bar and Stats */}
                  {(fileItem.status === 'uploading' || fileItem.status === 'completed') && (
                    <div className="space-y-1">
                      <div className="h-2 rounded-full bg-dark-600">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            fileItem.status === 'completed' 
                              ? 'bg-green-500' 
                              : 'bg-primary-500'
                          }`}
                          style={{ width: `${fileItem.progress}%` }}
                        />
                      </div>
                      
                      <div className="flex justify-between text-xs text-dark-400">
                        <span>{fileItem.progress}%</span>
                        {fileItem.status === 'uploading' && fileItem.speed && (
                          <div className="flex space-x-3">
                            <span>{formatSpeed(fileItem.speed)}</span>
                            {fileItem.eta && fileItem.eta > 0 && (
                              <span>ETA: {formatETA(fileItem.eta)}</span>
                            )}
                          </div>
                        )}
                        {fileItem.uploadedBytes && fileItem.totalBytes && (
                          <span>
                            {formatFileSize(fileItem.uploadedBytes)} / {formatFileSize(fileItem.totalBytes)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Error Message */}
                  {fileItem.status === 'error' && fileItem.error && (
                    <p className="text-xs text-red-400 mt-2">{fileItem.error}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
        />
      </div>
    </div>
  )
}